/**
 * Backend tenant POPIA officers API — current-tenant Information Officer
 * + Deputy Information Officer surfacing.
 *
 * Wraps GET /v1/tenants/{tenantId}/popia-officers.
 *
 * POPIA s55-56 requires the Information Officer (and any Deputy) to be
 * publicly identifiable to data subjects. The product UI must therefore
 * expose the name + contact channel of both roles wherever the data
 * subject acts on their POPIA rights — i.e. /app/privacy.
 *
 * The deputy block is null when no Deputy IO has been designated; the
 * caller renders an "Information Officer details pending designation"
 * fallback if the IO block is also missing (or the request 404s on a
 * tenant that hasn't designated officers yet).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from './client';
import { useTenantMe } from './tenants';

export interface PopiaOfficer {
  /** Public-facing officer name — backend `PopiaOfficerView.displayName`. */
  displayName: string;
  /** Which POPIA role this block represents. */
  role: 'io' | 'deputy-io';
  /** Public contact channel — an alias email, NEVER the personal one; null
   *  when the tenant designated an officer but published no contact email.
   *  (The backend `tenant-popia-officers-get` returns exactly these three
   *  fields — the earlier `{name,email,phone}` shape was a mismatch that left
   *  this block unpopulated.) */
  contactEmail: string | null;
}

export interface PopiaOfficersResponse {
  /** Information Officer — required by POPIA s55. */
  informationOfficer: PopiaOfficer | null;
  /** Deputy Information Officer — optional under POPIA s56. */
  deputyInformationOfficer: PopiaOfficer | null;
  correlationId: string;
}

/**
 * Read the current tenant's POPIA officers from the tenant context. The
 * query is disabled until `useTenantMe` resolves the tenant_id, so the
 * `/v1/tenants/{tenantId}/popia-officers` URL is always well-formed.
 *
 * 404 from the backend (officers not yet designated) is surfaced as the
 * regular `isError` state — the consumer renders the "pending designation"
 * placeholder rather than treating it as a hard failure.
 */
export function useTenantPopiaOfficers() {
  const tenantQ = useTenantMe();
  const tenantId = tenantQ.data?.tenant_id;
  return useQuery<PopiaOfficersResponse, ApiError>({
    queryKey: ['tenants', tenantId ?? null, 'popia-officers'],
    queryFn: ({ signal }) =>
      api<PopiaOfficersResponse>(
        `/api/v1/tenants/${encodeURIComponent(tenantId!)}/popia-officers`,
        { signal },
      ),
    enabled: !!tenantId,
    // 404 = no officers designated → don't keep retrying. Other 4xx/5xx
    // follow the global QueryClient retry policy.
    retry: (count, error) => {
      if (error instanceof ApiError && error.status === 404) return false;
      return count < 2;
    },
  });
}

// ── Designation (super_admin + step-up) ──────────────────────────────────────
//
// FE-9 (Henry R2-010): the Privacy Centre showed a permanent "pending
// designation" because no UI ever called the backend designation endpoints.
// These mutations wrap:
//   - POST /v1/admin/users/{id}/io-designate         (Information Officer)
//   - POST /v1/admin/users/{id}/deputy-io-designate  (Deputy IO)
// Both are super_admin-only + step-up gated server-side
// (see backend/src/handlers/admin-io-designate.ts). The handler bounds
// `effectiveAt` to [now-1d, now+7d] and requires a `reason` (1-2000 chars).

export type PopiaRole = 'io' | 'deputy-io';

export interface DesignateIoInput {
  /** Cosmos UserDoc id of the person being designated. */
  userId: string;
  /** ISO 8601 datetime WITH offset; must fall within [now-1d, now+7d]. */
  effectiveAt: string;
  /** Free-text rationale captured on the POPIA s55 audit row. */
  reason: string;
  /** Optional public contact email surfaced via /popia-officers; when omitted
   *  the public endpoint falls back to a tenant alias (never the personal email). */
  popiaContactEmail?: string;
  /** Optional second-witness userId for 4-eye appointment practice. */
  witnessUserId?: string;
}

export interface DesignateIoResponse {
  user: {
    id: string;
    popiaRole: PopiaRole;
    popiaRoleEffectiveAt: string;
  };
  correlationId: string;
}

function buildDesignatePath(role: PopiaRole, userId: string): string {
  const suffix = role === 'io' ? 'io-designate' : 'deputy-io-designate';
  return `/api/v1/admin/users/${encodeURIComponent(userId)}/${suffix}`;
}

/**
 * Designate the tenant's Information Officer (`role: 'io'`) or Deputy
 * Information Officer (`role: 'deputy-io'`). On success the cached
 * `popia-officers` query is invalidated so the Privacy Centre reflects the
 * new designation on next render.
 */
export function useDesignatePopiaOfficer(role: PopiaRole) {
  const qc = useQueryClient();
  return useMutation<DesignateIoResponse, ApiError, DesignateIoInput>({
    mutationFn: ({ userId, ...body }) =>
      api<DesignateIoResponse>(buildDesignatePath(role, userId), {
        method: 'POST',
        body,
      }),
    onSuccess: () => {
      // Officers are keyed by tenantId; invalidate the whole family so the
      // (single-tenant) cached entry refetches regardless of its tenant key.
      void qc.invalidateQueries({ queryKey: ['tenants'] });
    },
  });
}
